import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Drawer, Tabs, Table, Tag, Space, Typography, Descriptions, Select,
  Alert, Empty, Collapse, Spin,
} from "antd";
import { Button, Toast } from "../../ui";
import { useTheme } from "../../../contexts/ThemeContext";
import ExcelAiService from "../../../services/excelAiService";

const { Text, Paragraph } = Typography;

const SEVERIDADE = {
  2: { rotulo: "Não carregado", cor: "error", ordem: 0 },
  1: { rotulo: "Atenção", cor: "warning", ordem: 1 },
  0: { rotulo: "Informativa", cor: "default", ordem: 2 },
};

const rotuloSeveridade = (s) => SEVERIDADE[s]?.rotulo || "—";

/**
 * Detalhe de um processamento: o que aconteceu e o que a IA entendeu de cada arquivo.
 *
 * A aba de pendências é uma lista de alertas, com <b>uma única exceção acionável</b>: categoria
 * que a IA não conseguiu vincular a uma conta de DRE. Ali o usuário escolhe a conta, a escolha é
 * memorizada por categoria e passa a valer para os processamentos seguintes desta empresa.
 *
 * Para todo o resto a correção é na planilha — o usuário ajusta o arquivo e envia de novo. Não
 * adianta oferecer controle para pendência cuja decisão o pipeline não sabe aplicar: ele
 * resolveria, reprocessaria e veria o mesmo problema de volta.
 *
 * A aba "Arquivos" é o que dá confiança no produto — é onde o cliente vê *por que* o número é
 * aquele: qual coluna virou qual campo, com que confiança.
 */
/** Tipo da única pendência acionável: a decisão dela o pipeline sabe aplicar e memorizar. */
const CATEGORIA_SEM_DRE = "CATEGORIA_SEM_DRE";

/** Valor do select quando o usuário não quer vincular. Não gera decisão nem memorização. */
const SEM_CONTA = "";

const ExcelAiRunDetailDrawer = ({ aberto, syncId, run, podeEditar = true, onFechar, onReprocessado }) => {
  const { theme } = useTheme();
  const [detalhe, setDetalhe] = useState(null);
  const [pendencias, setPendencias] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // codDre escolhido por pendência. Ausente = "Deixar sem conta", que é o padrão.
  const [escolhas, setEscolhas] = useState({});

  const runId = run?.runId;

  const carregar = useCallback(async () => {
    if (!aberto || !syncId || !runId) return;
    try {
      setCarregando(true);
      setEscolhas({});
      const [d, p] = await Promise.all([
        ExcelAiService.obterRun(syncId, runId),
        ExcelAiService.listarPendencias(syncId, runId),
      ]);
      setDetalhe(d);
      setPendencias(Array.isArray(p) ? p : []);
    } catch (erro) {
      console.error("Erro ao carregar detalhe do processamento:", erro);
      Toast.error("Não foi possível carregar o detalhe deste processamento.");
    } finally {
      setCarregando(false);
    }
  }, [aberto, syncId, runId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const naoCarregadas = useMemo(
    () => pendencias.filter((p) => p.severidade === 2 && p.status === 0),
    [pendencias],
  );

  const semVinculo = useMemo(
    () => pendencias.filter((p) => p.tipo === CATEGORIA_SEM_DRE && p.status === 0),
    [pendencias],
  );

  const temEscolha = useMemo(
    () => Object.values(escolhas).some((v) => v !== SEM_CONTA && v != null),
    [escolhas],
  );

  /**
   * Grava as vinculações escolhidas e reprocessa.
   *
   * Só sobe o que o usuário de fato escolheu: "Deixar sem conta" é ausência de decisão, não uma
   * decisão de não vincular — a categoria continua sendo perguntada no processamento seguinte.
   */
  const salvarEReprocessar = async () => {
    try {
      setSalvando(true);

      const resolucoes = Object.entries(escolhas)
        .filter(([, codDre]) => codDre !== SEM_CONTA && codDre != null)
        .map(([pendenciaId, codDre]) => ({
          pendenciaId,
          acao: 5, // MapearCategoriaDre
          valor: String(codDre),
        }));

      if (resolucoes.length === 0) {
        Toast.info("Nenhuma conta foi escolhida.");
        return;
      }

      await ExcelAiService.resolverPendencias(syncId, runId, resolucoes);

      // Gravar a decisão não reprocessa sozinho: quem tem os arquivos e sabe gerar um SAS novo é o
      // Backend, e o do processamento anterior já expirou. São duas chamadas de propósito.
      const resultado = await ExcelAiService.processar(syncId);

      Toast.success(
        resultado?.success
          ? "Vinculações salvas. Reprocessando os arquivos."
          : "Vinculações salvas. Inicie o processamento quando quiser.",
      );

      onReprocessado?.();
      onFechar?.();
    } catch (erro) {
      console.error("Erro ao salvar vinculações de DRE:", erro);
      Toast.error("Não foi possível salvar as vinculações.");
    } finally {
      setSalvando(false);
    }
  };

  const colunasPendencia = [
    {
      title: "Severidade",
      dataIndex: "severidade",
      key: "severidade",
      width: 120,
      render: (s) => <Tag color={SEVERIDADE[s]?.cor}>{rotuloSeveridade(s)}</Tag>,
    },
    {
      title: "O que aconteceu",
      key: "mensagem",
      render: (_, p) => (
        <div>
          <Text style={{ fontSize: 13 }}>{p.mensagem}</Text>
          <div style={{ marginTop: 2 }}>
            <Text style={{ fontSize: 12, color: theme.textSecondary }}>
              {[p.arquivo, p.aba, p.coluna].filter(Boolean).join(" › ")}
              {p.ocorrencias > 1 && ` · ${p.ocorrencias.toLocaleString("pt-BR")} ocorrências`}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Conta do DRE",
      key: "conta",
      width: 300,
      render: (_, p) => {
        // Alerta puro: a correção é na planilha, não aqui.
        if (p.tipo !== CATEGORIA_SEM_DRE || p.status !== 0) {
          return <Text style={{ color: theme.textSecondary }}>—</Text>;
        }
        if (!podeEditar) return <Text style={{ color: theme.textSecondary }}>—</Text>;

        const opcoes = p.sugestoes || [];

        return (
          <Select
            size="small"
            style={{ width: "100%" }}
            value={escolhas[p.id] ?? SEM_CONTA}
            onChange={(valor) => setEscolhas((atual) => ({ ...atual, [p.id]: valor }))}
            options={[
              { value: SEM_CONTA, label: "Deixar sem conta" },
              ...opcoes.map((o) => ({
                value: String(o.valor ?? o.value),
                label: o.confianca
                  ? `${o.rotulo ?? o.label} · sugestão (${Math.round(o.confianca * 100)}%)`
                  : (o.rotulo ?? o.label),
              })),
            ]}
          />
        );
      },
    },
  ];

  const abaResumo = (
    <Descriptions column={1} size="small" bordered>
      <Descriptions.Item label="Status">{detalhe?.status || run?.status || "—"}</Descriptions.Item>
      <Descriptions.Item label="Estágio">{detalhe?.estagio || "—"}</Descriptions.Item>
      <Descriptions.Item label="Solicitado por">{detalhe?.solicitadoPor || "—"}</Descriptions.Item>
      <Descriptions.Item label="Registros carregados">
        {detalhe?.totalRegistros?.toLocaleString("pt-BR") || "—"}
      </Descriptions.Item>
      {detalhe?.resumoPorTabela && (
        <Descriptions.Item label="Por tabela">
          <Space wrap>
            {Object.entries(detalhe.resumoPorTabela).map(([tabela, qtd]) => (
              <Tag key={tabela}>
                {tabela}: {Number(qtd).toLocaleString("pt-BR")}
              </Tag>
            ))}
          </Space>
        </Descriptions.Item>
      )}
      {detalhe?.erroMensagem && (
        <Descriptions.Item label="Erro">
          <Text type="danger">{detalhe.erroMensagem}</Text>
        </Descriptions.Item>
      )}
    </Descriptions>
  );

  const abaArquivos = (detalhe?.arquivos || []).length === 0 ? (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sem detalhe de arquivos" />
  ) : (
    <Collapse
      accordion
      items={(detalhe.arquivos || []).map((arquivo) => ({
        key: arquivo.fileId || arquivo.nomeOriginal,
        label: (
          <Space wrap>
            <Text style={{ fontSize: 13, fontWeight: 500 }}>{arquivo.nomeOriginal}</Text>
            {/* O tipo é por aba, não por arquivo: uma planilha pode ter títulos numa aba e vendas
                em outra. Mostrar um tipo só para o arquivo inteiro seria mentira. */}
            {[...new Set((arquivo.abas || []).map((a) => a.tipoDetectado))].map((tipo) => (
              <Tag key={tipo}>{tipo || "Não classificado"}</Tag>
            ))}
            {(arquivo.abas || []).length === 0 && <Tag color="warning">{arquivo.status}</Tag>}
          </Space>
        ),
        children: (
          <>
            {(arquivo.abas || []).map((aba) => (
              <div key={aba.nomeAba} style={{ marginBottom: 12 }}>
                <Paragraph style={{ marginBottom: 4, fontSize: 13 }}>
                  <b>{aba.nomeAba}</b> · cabeçalho na linha {aba.linhaCabecalho ?? "?"} ·{" "}
                  {(aba.linhasAproveitadas ?? 0).toLocaleString("pt-BR")} de{" "}
                  {(aba.linhasLidas ?? 0).toLocaleString("pt-BR")} linhas aproveitadas
                </Paragraph>
                <Table
                  size="small"
                  rowKey={(c, i) => `${aba.nomeAba}-${c.origem}-${i}`}
                  pagination={false}
                  dataSource={aba.colunas || []}
                  columns={[
                    { title: "Coluna da planilha", dataIndex: "origem", key: "origem" },
                    {
                      title: "Vira",
                      dataIndex: "destino",
                      key: "destino",
                      render: (d) =>
                        d || <Text style={{ color: theme.textSecondary }}>não usada</Text>,
                    },
                    {
                      title: "Confiança",
                      dataIndex: "confianca",
                      key: "confianca",
                      width: 110,
                      render: (c) =>
                        c == null ? (
                          "—"
                        ) : (
                          <Text type={c < 0.7 ? "warning" : undefined}>
                            {Math.round(c * 100)}%
                          </Text>
                        ),
                    },
                  ]}
                />
              </div>
            ))}
          </>
        ),
      }))}
    />
  );

  const abaPendencias = (
    <>
      {naoCarregadas.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            naoCarregadas.length === 1
              ? "1 item não pôde ser carregado"
              : `${naoCarregadas.length} itens não puderam ser carregados`
          }
          description="O restante dos dados foi carregado normalmente. Ajuste a planilha e envie de novo para incluir o que faltou."
        />
      )}
      {semVinculo.length > 0 && podeEditar && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            semVinculo.length === 1
              ? "1 categoria sem conta de DRE"
              : `${semVinculo.length} categorias sem conta de DRE`
          }
          description="Escolha a conta de cada uma para que os lançamentos entrem no DRE. A escolha fica memorizada e vale para os próximos processamentos desta empresa."
          action={
            <Button
              type="primary"
              size="small"
              loading={salvando}
              disabled={!temEscolha}
              onClick={salvarEReprocessar}
            >
              Salvar e reprocessar
            </Button>
          }
        />
      )}

      <Table
        size="small"
        rowKey="id"
        columns={colunasPendencia}
        dataSource={[...pendencias].sort(
          (a, b) => (SEVERIDADE[a.severidade]?.ordem ?? 9) - (SEVERIDADE[b.severidade]?.ordem ?? 9),
        )}
        pagination={false}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhuma pendência" />
          ),
        }}
      />
    </>
  );

  return (
    <Drawer
      open={aberto}
      onClose={onFechar}
      width={880}
      title="Detalhes do processamento"
    >
      {carregando ? (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin />
        </div>
      ) : (
        <Tabs
          items={[
            { key: "resumo", label: "Resumo", children: abaResumo },
            { key: "arquivos", label: "Arquivos", children: abaArquivos },
            {
              key: "pendencias",
              label: `Pendências${pendencias.length ? ` (${pendencias.length})` : ""}`,
              children: abaPendencias,
            },
          ]}
        />
      )}
    </Drawer>
  );
};

export default ExcelAiRunDetailDrawer;
